<template>
  <v-menu v-model="menu" :close-on-content-click="false" location="bottom" min-width="350" :scrim="false"
    @update:model-value="handleMenuToggle">
    <template #activator="{ props }">
      <v-text-field v-bind="props" :model-value="displayText" :label="label" :placeholder="placeholder"
        :clearable="clearable" :variant="variant" :density="density" :hide-details="hideDetails" readonly
        prepend-inner-icon="mdi-calendar" @click:clear="handleClear" rounded="pill"></v-text-field>
    </template>

    <v-card>
      <!-- 年份选择栏 -->
      <v-toolbar density="compact" flat>
        <v-btn icon="mdi-chevron-left" @click="prevYear"></v-btn>
        <v-toolbar-title>{{ displayYear }}</v-toolbar-title>
        <v-btn icon="mdi-chevron-right" @click="nextYear"></v-btn>
      </v-toolbar>

      <!-- 季度卡片网格 -->
      <v-container fluid class="pa-2">
        <v-row dense>
          <v-col v-for="quarter in quarters" :key="quarter.value" cols="6">
            <v-card :variant="isQuarterSelected(quarter.value) ? 'elevated' : 'outlined'"
              :color="isQuarterSelected(quarter.value) ? '#2655f1' : ''" @click="toggleQuarter(quarter.value)"
              class="text-center cursor-pointer" flat>
              <v-card-text>{{ quarter.label }}</v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- 操作按钮 -->
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="cancelSelection">
          取消
        </v-btn>
        <v-btn variant="text" color="#2655f1" @click="confirmSelection">
          确认
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

// Props 定义
const props = defineProps({
  modelValue: {
    type: Array, // 预期格式: [{ year: 2024, quarter: 1 }, ...]
    default: () => []
  },
  label: {
    type: String,
    default: '选择季度'
  },
  placeholder: {
    type: String,
    default: '请选择季度'
  },
  multiple: {
    type: Boolean,
    default: false // 是否支持多选季度
  },
  clearable: {
    type: Boolean,
    default: true
  },
  variant: {
    type: String,
    default: 'outlined'
  },
  density: {
    type: String,
    default: 'compact'
  },
  hideDetails: {
    type: Boolean,
    default: false
  },
  minYear: {
    type: Number,
    default: 2020
  },
  maxYear: {
    type: Number,
    default: 2030
  },
  // 首次打开时显示的年份（默认当前年）
  initialYear: {
    type: Number,
    default: () => new Date().getFullYear()
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'change'])

// 季度数据
const quarters = [
  { label: '第一季度 (Q1)', value: 1, months: [1, 2, 3] },
  { label: '第二季度 (Q2)', value: 2, months: [4, 5, 6] },
  { label: '第三季度 (Q3)', value: 3, months: [7, 8, 9] },
  { label: '第四季度 (Q4)', value: 4, months: [10, 11, 12] }
]

// 状态
const menu = ref(false)
const currentYear = ref(props.initialYear)

// 临时选中的季度（用于菜单内操作）
const tempSelectedQuarters = ref(new Map()) // Map<year, Set<quarter>>

// 备份原始选中值（用于取消时恢复）
const originalSelectedQuarters = ref(new Map())

// 初始化选中值
watch(() => props.modelValue, (newVal) => {
  const map = new Map()
  if (Array.isArray(newVal)) {
    newVal.forEach(item => {
      if (item && typeof item.year === 'number' && typeof item.quarter === 'number') {
        if (!map.has(item.year)) {
          map.set(item.year, new Set())
        }
        map.get(item.year).add(item.quarter)
      }
    })
  }
  tempSelectedQuarters.value = map
  originalSelectedQuarters.value = map // 保存原始值
}, { immediate: true })

// 处理菜单打开/关闭
function handleMenuToggle(isOpen) {
  if (isOpen) {
    // 打开菜单时，备份当前选中值
    backupOriginalSelection()
    // 重置为当前选中值（避免上次取消的残留）
    resetToOriginal()
  } else {
    // 菜单关闭时（通过点击外部关闭），恢复原始值
    resetToOriginal()
  }
}

// 备份原始选中值
function backupOriginalSelection() {
  // 深拷贝 Map
  const map = new Map()
  for (const [year, quartersSet] of tempSelectedQuarters.value.entries()) {
    map.set(year, new Set(quartersSet))
  }
  originalSelectedQuarters.value = map
}

// 恢复到原始选中值
function resetToOriginal() {
  // 深拷贝原始值到临时值
  const map = new Map()
  for (const [year, quartersSet] of originalSelectedQuarters.value.entries()) {
    map.set(year, new Set(quartersSet))
  }
  tempSelectedQuarters.value = map
}

// 显示年份
const displayYear = computed(() => currentYear.value)

// 显示文本
const displayText = computed(() => {
  const map = tempSelectedQuarters.value
  if (map.size === 0) return ''

  const items = []
  for (const [year, quartersSet] of map.entries()) {
    const quartersList = Array.from(quartersSet).sort((a, b) => a - b)
    quartersList.forEach(q => {
      items.push(`${year} Q${q}`)
    })
  }
  return items.join(', ')
})

// 判断当前年份的某个季度是否被选中
function isQuarterSelected(quarter) {
  return tempSelectedQuarters.value.get(currentYear.value)?.has(quarter) || false
}

// 切换季度选择
function toggleQuarter(quarter) {
  const year = currentYear.value
  const map = new Map(tempSelectedQuarters.value)

  if (props.multiple) {
    // 多选模式
    if (!map.has(year)) {
      map.set(year, new Set([quarter]))
    } else {
      const set = new Set(map.get(year))
      if (set.has(quarter)) {
        set.delete(quarter)
        if (set.size === 0) {
          map.delete(year)
        } else {
          map.set(year, set)
        }
      } else {
        set.add(quarter)
        map.set(year, set)
      }
    }
  } else {
    // 单选模式
    map.clear()
    map.set(year, new Set([quarter]))
  }

  tempSelectedQuarters.value = map
}

// 确认选择
function confirmSelection() {
  // 将临时值转换为数组格式
  const result = []
  for (const [year, quartersSet] of tempSelectedQuarters.value.entries()) {
    const quartersList = Array.from(quartersSet)
    quartersList.forEach(quarter => {
      result.push({ year, quarter })
    })
  }

  // 更新原始备份（因为确认后，当前值成为新的原始值）
  originalSelectedQuarters.value = new Map(tempSelectedQuarters.value)

  emit('update:modelValue', result)
  emit('change', result)
  menu.value = false
}

// 取消选择
function cancelSelection() {
  // 恢复原始选中值
  resetToOriginal()
  menu.value = false
}

// 清除
function handleClear() {
  tempSelectedQuarters.value = new Map()
  confirmSelection() // 清除后直接确认
}

// 年份切换
function prevYear() {
  if (currentYear.value > props.minYear) {
    currentYear.value--
  }
}
function nextYear() {
  if (currentYear.value < props.maxYear) {
    currentYear.value++
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

:deep(.v-input__details) {
  display: none;
}
</style>
